import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatLogTimestamp } from '../utils/dateUtils';

function NotificationLogs({ token, timezone }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ hasMore: false, nextCursor: null, total: 0 });
  const [selectedLog, setSelectedLog] = useState(null);

  // Filter and sort state
  const [limit, setLimit] = useState(50);
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Load stats
  useEffect(() => {
    fetchStats();
  }, []);

  // Load logs when filters change
  useEffect(() => {
    fetchLogs(true);
  }, [limit, sortBy, sortOrder, searchText]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/notification-logs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching notification stats:', error);
    }
  };

  const fetchLogs = async (reset = false) => {
    try {
      setLoading(true);
      const params = {
        limit,
        sortBy,
        sortOrder,
        search: searchText
      };

      if (!reset && pagination.nextCursor) {
        params.cursor = pagination.nextCursor;
      }

      const response = await api.get('/notification-logs', { params });

      if (reset) {
        setLogs(response.data.logs);
      } else {
        setLogs(prev => [...prev, ...response.data.logs]);
      }

      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching notification logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleSearch = () => {
    setSearchText(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchText('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatLogTimestamp(dateString, timezone);
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <span style={{ color: '#ccc' }}>⇅</span>;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h2 style={{ marginBottom: '1.5rem' }}>Notification Logs</h2>

      {/* Statistics */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div style={{ padding: '1rem', background: 'var(--color-card-bg)', borderRadius: '0.5rem', border: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Total Notifications</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.5rem' }}>{stats.total_notifications || 0}</div>
          </div>
          <div style={{ padding: '1rem', background: 'var(--color-card-bg)', borderRadius: '0.5rem', border: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Emails Sent</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.5rem', color: 'var(--color-primary)' }}>{stats.successful_emails || 0}</div>
          </div>
          <div style={{ padding: '1rem', background: 'var(--color-card-bg)', borderRadius: '0.5rem', border: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>SMS Sent</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.5rem', color: 'var(--color-primary)' }}>{stats.successful_sms || 0}</div>
          </div>
          <div style={{ padding: '1rem', background: 'var(--color-card-bg)', borderRadius: '0.5rem', border: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Failed</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.5rem', color: 'var(--color-danger)' }}>{stats.failed_sends || 0}</div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div style={{
        marginBottom: '1.5rem',
        padding: '1rem',
        background: 'var(--color-card-bg)',
        borderRadius: '0.5rem',
        border: '1px solid var(--color-border)'
      }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '250px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Search (contains)
            </label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search all fields..."
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--color-border)',
                borderRadius: '0.25rem'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Results per page
            </label>
            <input
              type="number"
              min="10"
              max="500"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value) || 50)}
              style={{
                width: '100px',
                padding: '0.5rem',
                border: '1px solid var(--color-border)',
                borderRadius: '0.25rem'
              }}
            />
          </div>
          <button
            onClick={handleSearch}
            style={{
              padding: '0.625rem 1.25rem',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '9999px',
              cursor: 'pointer'
            }}
          >
            Search
          </button>
          {searchText && (
            <button
              onClick={handleClearSearch}
              style={{
                padding: '0.625rem 1.25rem',
                background: 'var(--color-text-secondary)',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          )}
        </div>
        {searchText && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            Searching for: <strong>{searchText}</strong>
          </div>
        )}
      </div>

      {/* Logs Table */}
      <div style={{ overflowX: 'auto', background: 'var(--color-card-bg)', borderRadius: '0.5rem', border: '1px solid var(--color-border)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--color-background)' }}>
              <th onClick={() => handleSort('id')} style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer', borderBottom: '2px solid var(--color-border)' }}>
                ID <SortIcon column="id" />
              </th>
              <th onClick={() => handleSort('notification_type')} style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer', borderBottom: '2px solid var(--color-border)' }}>
                Type <SortIcon column="notification_type" />
              </th>
              <th onClick={() => handleSort('destination')} style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer', borderBottom: '2px solid var(--color-border)' }}>
                Destination <SortIcon column="destination" />
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid var(--color-border)' }}>
                Subject/Body
              </th>
              <th onClick={() => handleSort('source')} style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer', borderBottom: '2px solid var(--color-border)' }}>
                Source <SortIcon column="source" />
              </th>
              <th onClick={() => handleSort('status')} style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer', borderBottom: '2px solid var(--color-border)' }}>
                Status <SortIcon column="status" />
              </th>
              <th onClick={() => handleSort('sent_at')} style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer', borderBottom: '2px solid var(--color-border)' }}>
                Sent At <SortIcon column="sent_at" />
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && logs.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                  Loading...
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                  No notifications found
                </td>
              </tr>
            ) : (
              logs.map(log => (
                <tr
                  key={log.id}
                  onClick={() => setSelectedLog(log)}
                  style={{ cursor: 'pointer', borderBottom: '1px solid var(--color-border)' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-background)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                >
                  <td style={{ padding: '0.75rem' }}>{log.id}</td>
                  <td style={{ padding: '0.75rem' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      background: log.notification_type === 'email' ? 'var(--color-info)' : 'var(--color-warning)',
                      color: 'white'
                    }}>
                      {log.notification_type}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{log.destination}</td>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem', maxWidth: '300px' }}>
                    {log.subject ? (
                      <div>
                        <strong>{truncateText(log.subject, 50)}</strong>
                        <div style={{ color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
                          {truncateText(log.body, 80)}
                        </div>
                      </div>
                    ) : (
                      truncateText(log.body, 100)
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{log.source}</td>
                  <td style={{ padding: '0.75rem' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      background: log.status === 'sent' ? 'var(--color-success)' : 'var(--color-danger)',
                      color: 'white'
                    }}>
                      {log.status}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{formatDate(log.sent_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.hasMore && (
        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <button
            onClick={() => fetchLogs(false)}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
          <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            Showing {logs.length} of {pagination.total} notifications
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedLog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setSelectedLog(null)}
        >
          <div
            style={{
              background: 'var(--color-card-bg)',
              border: '1px solid var(--color-border)',
              borderRadius: '0.5rem',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: '2rem',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedLog(null)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: 'var(--color-text)',
                padding: '0.25rem',
                lineHeight: 1
              }}
            >
              ×
            </button>

            <h3 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Notification Details</h3>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                  ID
                </strong>
                <div>{selectedLog.id}</div>
              </div>

              <div>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                  Type
                </strong>
                <span style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  background: selectedLog.notification_type === 'email' ? 'var(--color-info)' : 'var(--color-warning)',
                  color: 'white'
                }}>
                  {selectedLog.notification_type}
                </span>
              </div>

              <div>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                  Destination
                </strong>
                <div>{selectedLog.destination}</div>
              </div>

              {selectedLog.subject && (
                <div>
                  <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                    Subject
                  </strong>
                  <div>{selectedLog.subject}</div>
                </div>
              )}

              <div>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                  Message Body
                </strong>
                <div style={{
                  padding: '0.75rem',
                  background: 'var(--color-background)',
                  borderRadius: '0.25rem',
                  maxHeight: '300px',
                  overflow: 'auto',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {selectedLog.body}
                </div>
              </div>

              <div>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                  Source
                </strong>
                <div>{selectedLog.source}</div>
              </div>

              <div>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                  Status
                </strong>
                <span style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  background: selectedLog.status === 'sent' ? 'var(--color-success)' : 'var(--color-danger)',
                  color: 'white'
                }}>
                  {selectedLog.status}
                </span>
              </div>

              {selectedLog.error_message && (
                <div>
                  <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                    Error Message
                  </strong>
                  <div style={{
                    padding: '0.75rem',
                    background: 'var(--color-danger)',
                    color: 'white',
                    borderRadius: '0.25rem',
                    fontSize: '0.875rem',
                    wordBreak: 'break-word'
                  }}>
                    {selectedLog.error_message}
                  </div>
                </div>
              )}

              {selectedLog.username && (
                <div>
                  <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                    User
                  </strong>
                  <div>{selectedLog.username} (ID: {selectedLog.user_id})</div>
                </div>
              )}

              <div>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                  Sent At
                </strong>
                <div>{formatDate(selectedLog.sent_at)}</div>
              </div>
            </div>

            <div style={{ marginTop: '2rem', textAlign: 'right' }}>
              <button
                onClick={() => setSelectedLog(null)}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'var(--color-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationLogs;
